import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

const STORAGE_KEY = 'yarlhub_login_target'

const resolveRedirect = (search: string) => {
  const params = new URLSearchParams(search)
  const redirectParam = params.get('redirect') || '/admin/obituaries'
  if (typeof window === 'undefined') {
    return redirectParam.startsWith('/') ? redirectParam : `/${redirectParam}`
  }
  try {
    const url = new URL(redirectParam, window.location.origin)
    if (url.origin !== window.location.origin) {
      return '/admin/obituaries'
    }
    const relative = `${url.pathname}${url.search}${url.hash}`
    return relative || '/admin/obituaries'
  } catch {
    return redirectParam.startsWith('/') ? redirectParam : `/${redirectParam}`
  }
}

/*
 * Styles are scoped under `.yh-login` and intentionally self-contained:
 * the global reset in index.css (`* { padding: 0 }`) overrides PrimeReact's
 * layered component styles, which previously collapsed Card/Button paddings.
 */
const styles = `
  .yh-login {
    --yh-accent: #d35400;
    --yh-accent-2: #e67e22;
    --yh-accent-hover: #b94a00;
    --yh-bg: #f4f6f8;
    --yh-surface: #ffffff;
    --yh-text: #1a252f;
    --yh-muted: #64748b;
    --yh-border: #dbe1e8;
    --yh-input-bg: #ffffff;
    min-height: 100vh;
    display: flex;
    background: var(--yh-bg);
    color: var(--yh-text);
  }
  .dark-mode .yh-login {
    --yh-bg: #12181f;
    --yh-surface: #1c242d;
    --yh-text: #f1f5f9;
    --yh-muted: #94a3b8;
    --yh-border: #334155;
    --yh-input-bg: #111820;
  }

  /* ---------- Brand panel ---------- */
  .yh-login__brand {
    position: relative;
    flex: 1 1 50%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 3rem 3.5rem;
    color: #fff;
    overflow: hidden;
    background:
      radial-gradient(circle at 15% 85%, rgba(211, 84, 0, 0.45) 0%, transparent 45%),
      radial-gradient(circle at 85% 15%, rgba(52, 152, 219, 0.25) 0%, transparent 45%),
      linear-gradient(135deg, #1a252f 0%, #2c3e50 55%, #1a252f 100%);
  }
  .yh-login__brand::after {
    content: 'யா';
    position: absolute;
    right: -2rem;
    bottom: -5rem;
    font-family: serif;
    font-size: 22rem;
    font-weight: 700;
    line-height: 1;
    color: rgba(255, 255, 255, 0.04);
    pointer-events: none;
  }
  .yh-login__logo {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    color: inherit;
    text-decoration: none;
  }
  .yh-login__logo-mark {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: serif;
    font-weight: 700;
    font-size: 1.25rem;
    color: #fff;
    background: linear-gradient(135deg, var(--yh-accent) 0%, var(--yh-accent-2) 100%);
    box-shadow: 0 4px 15px rgba(211, 84, 0, 0.45);
  }
  .yh-login__logo-name {
    font-family: serif;
    font-weight: 700;
    font-size: 1.5rem;
    letter-spacing: 0.5px;
  }
  .yh-login__logo-sub {
    display: block;
    font-size: 0.75rem;
    opacity: 0.7;
    margin-top: -2px;
  }
  .yh-login__pitch {
    position: relative;
    z-index: 1;
    max-width: 30rem;
  }
  .yh-login__pitch h2 {
    font-size: 2.25rem;
    line-height: 1.2;
    font-weight: 700;
    margin-bottom: 1rem;
  }
  .yh-login__pitch p {
    font-size: 1.05rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.75);
    margin-bottom: 2rem;
  }
  .yh-login__features {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }
  .yh-login__features li {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    color: rgba(255, 255, 255, 0.9);
  }
  .yh-login__features i {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(211, 84, 0, 0.2);
    color: #f0a46b;
    flex-shrink: 0;
  }
  .yh-login__brand-foot {
    position: relative;
    z-index: 1;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.55);
  }

  /* ---------- Form panel ---------- */
  .yh-login__main {
    flex: 1 1 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1.5rem;
  }
  .yh-login__card {
    width: 100%;
    max-width: 420px;
    background: var(--yh-surface);
    border: 1px solid var(--yh-border);
    border-radius: 18px;
    padding: 2.5rem 2.25rem;
    box-shadow: 0 20px 45px -20px rgba(15, 23, 42, 0.25);
  }
  .yh-login__back {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.9rem;
    color: var(--yh-muted);
    text-decoration: none;
    margin-bottom: 1.75rem;
    transition: color 0.2s ease;
  }
  .yh-login__back:hover { color: var(--yh-accent); }
  .yh-login__title {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 0.4rem;
    color: var(--yh-text);
  }
  .yh-login__subtitle {
    color: var(--yh-muted);
    margin-bottom: 1.75rem;
  }
  .yh-login__form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }
  .yh-login__alert {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.85rem 1rem;
    border-radius: 10px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    font-size: 0.925rem;
    line-height: 1.4;
  }
  .dark-mode .yh-login__alert {
    background: rgba(185, 28, 28, 0.15);
    border-color: rgba(248, 113, 113, 0.4);
    color: #fca5a5;
  }
  .yh-login__alert i { margin-top: 2px; }
  .yh-login__field {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }
  .yh-login__label {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--yh-text);
  }
  .yh-login__control {
    position: relative;
    display: flex;
    align-items: center;
  }
  .yh-login__control > .pi:first-child {
    position: absolute;
    left: 0.95rem;
    color: var(--yh-muted);
    pointer-events: none;
  }
  .yh-login__input {
    width: 100%;
    height: 48px;
    padding: 0 1rem 0 2.75rem;
    font: inherit;
    font-size: 1rem;
    color: var(--yh-text);
    background: var(--yh-input-bg);
    border: 1px solid var(--yh-border);
    border-radius: 10px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .yh-login__input--with-toggle { padding-right: 3rem; }
  .yh-login__input::placeholder { color: var(--yh-muted); opacity: 0.7; }
  .yh-login__input:focus {
    border-color: var(--yh-accent);
    box-shadow: 0 0 0 3px rgba(211, 84, 0, 0.18);
  }
  .yh-login__input:disabled { opacity: 0.6; cursor: not-allowed; }
  .yh-login__toggle {
    position: absolute;
    right: 0.4rem;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--yh-muted);
    cursor: pointer;
  }
  .yh-login__toggle:hover { color: var(--yh-accent); background: rgba(211, 84, 0, 0.08); }
  .yh-login__toggle:focus-visible { outline: 2px solid var(--yh-accent); outline-offset: 1px; }
  .yh-login__submit {
    margin-top: 0.5rem;
    height: 50px;
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    border: none;
    border-radius: 10px;
    font: inherit;
    font-size: 1.05rem;
    font-weight: 700;
    color: #fff;
    background: linear-gradient(135deg, var(--yh-accent) 0%, var(--yh-accent-2) 100%);
    box-shadow: 0 8px 20px -8px rgba(211, 84, 0, 0.7);
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;
  }
  .yh-login__submit:hover:not(:disabled) {
    filter: brightness(1.05);
    transform: translateY(-1px);
    box-shadow: 0 12px 24px -10px rgba(211, 84, 0, 0.8);
  }
  .yh-login__submit:focus-visible { outline: 3px solid rgba(211, 84, 0, 0.45); outline-offset: 2px; }
  .yh-login__submit:disabled { opacity: 0.65; cursor: not-allowed; }
  .yh-login__divider {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 1.75rem 0 1.25rem;
    color: var(--yh-muted);
    font-size: 0.85rem;
  }
  .yh-login__divider::before,
  .yh-login__divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--yh-border);
  }
  .yh-login__register {
    width: 100%;
    height: 46px;
    border-radius: 10px;
    border: 1px solid var(--yh-border);
    background: transparent;
    font: inherit;
    font-weight: 600;
    color: var(--yh-text);
    cursor: pointer;
    transition: border-color 0.2s ease, color 0.2s ease, background 0.2s ease;
  }
  .yh-login__register:hover {
    border-color: var(--yh-accent);
    color: var(--yh-accent);
    background: rgba(211, 84, 0, 0.05);
  }
  .yh-login__register:focus-visible { outline: 2px solid var(--yh-accent); outline-offset: 2px; }

  @media (max-width: 960px) {
    .yh-login { flex-direction: column; }
    .yh-login__brand {
      flex: 0 0 auto;
      padding: 1.25rem 1.5rem;
    }
    .yh-login__brand::after,
    .yh-login__pitch,
    .yh-login__brand-foot { display: none; }
    .yh-login__main {
      flex: 1 1 auto;
      justify-content: flex-start;
      padding-top: 2rem;
    }
  }
  @media (max-width: 480px) {
    .yh-login__card {
      padding: 2rem 1.25rem;
      border-radius: 14px;
    }
  }
`

const Login: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { authenticated, ready, login, register, error } = useAuth()
  const [submitting, setSubmitting] = React.useState(false)
  const [localError, setLocalError] = React.useState<string | null>(null)
  const [showPassword, setShowPassword] = React.useState(false)

  const [identifier, setIdentifier] = React.useState('')
  const [password, setPassword] = React.useState('')

  const redirect = React.useMemo(() => resolveRedirect(location.search), [location.search])

  React.useEffect(() => {
    if (!ready || !authenticated) return
    let target = redirect
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      if (stored) {
        sessionStorage.removeItem(STORAGE_KEY)
        target = stored
      }
    } catch {
      // ignore
    }
    navigate(target, { replace: true })
  }, [authenticated, ready, navigate, redirect])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting) return
    if (!identifier.trim() || !password) {
      setLocalError('Please enter your username or email and your password.')
      return
    }
    setSubmitting(true)
    setLocalError(null)
    try {
      try {
        sessionStorage.setItem(STORAGE_KEY, redirect)
      } catch {
        // ignore
      }
      await login(identifier.trim(), password)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login fehlgeschlagen'
      setLocalError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const busy = submitting || !ready
  const combinedError = localError || error

  return (
    <div className="yh-login">
      <style>{styles}</style>

      <aside className="yh-login__brand">
        <Link to="/" className="yh-login__logo" aria-label="YarlHub home">
          <span className="yh-login__logo-mark">யா</span>
          <span>
            <span className="yh-login__logo-name">YarlHub</span>
            <span className="yh-login__logo-sub">Tamil Community Portal</span>
          </span>
        </Link>

        <div className="yh-login__pitch">
          <h2>Welcome back to your community.</h2>
          <p>Sign in to manage news, obituaries and local Tamil businesses on YarlHub.</p>
          <ul className="yh-login__features">
            <li><i className="pi pi-align-left" aria-hidden="true" /> Publish and curate the latest news</li>
            <li><i className="pi pi-users" aria-hidden="true" /> Manage obituaries with care</li>
            <li><i className="pi pi-shopping-bag" aria-hidden="true" /> Keep shop listings up to date</li>
          </ul>
        </div>

        <div className="yh-login__brand-foot">&copy; {new Date().getFullYear()} YarlHub</div>
      </aside>

      <main className="yh-login__main">
        <div className="yh-login__card">
          <Link to="/" className="yh-login__back">
            <i className="pi pi-arrow-left" aria-hidden="true" /> Back to YarlHub
          </Link>

          <h1 className="yh-login__title">Admin Login</h1>
          <p className="yh-login__subtitle">Please login to manage YarlHub content.</p>

          <form onSubmit={handleSubmit} className="yh-login__form" noValidate>
            {combinedError && (
              <div className="yh-login__alert" role="alert">
                <i className="pi pi-exclamation-circle" aria-hidden="true" />
                <span>{combinedError}</span>
              </div>
            )}

            <div className="yh-login__field">
              <label htmlFor="identifier" className="yh-login__label">Username or Email</label>
              <div className="yh-login__control">
                <i className="pi pi-user" aria-hidden="true" />
                <input
                  id="identifier"
                  name="username"
                  type="text"
                  className="yh-login__input"
                  placeholder="name@example.com"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  autoFocus
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  disabled={busy}
                />
              </div>
            </div>

            <div className="yh-login__field">
              <label htmlFor="password" className="yh-login__label">Password</label>
              <div className="yh-login__control">
                <i className="pi pi-lock" aria-hidden="true" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className="yh-login__input yh-login__input--with-toggle"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                />
                <button
                  type="button"
                  className="yh-login__toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  <i className={showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'} aria-hidden="true" />
                </button>
              </div>
            </div>

            <button type="submit" className="yh-login__submit" disabled={busy}>
              <i className={busy ? 'pi pi-spin pi-spinner' : 'pi pi-sign-in'} aria-hidden="true" />
              {submitting ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="yh-login__divider">New to YarlHub?</div>
          <button
            type="button"
            className="yh-login__register"
            onClick={(e) => {
              e.preventDefault()
              register()
            }}
          >
            Create an Account
          </button>
        </div>
      </main>
    </div>
  )
}

export default Login
